import { GtmhubController, IRestLayerRequest } from "@gtmhub/core";
import { IIndicator } from "@gtmhub/error-handling";
import { getGoogleCharts } from "@gtmhub/insights";
import { ChartSettingsModalOpener } from "@gtmhub/lists/services/chart-settings-modal-opener";
import { ListQueryService } from "@gtmhub/lists/services/list-query.service";
import { ICollection } from "@gtmhub/models";
import { IComponentOptions, IController, IOnChangesObjectOf, IPromise, IQService, IScope, ITimeoutService } from "angular";
import { IdMap } from "@gtmhub/util/utils";
import {
  aggregationSettingToGoogleAggregationMap as AggregationMap,
  chartViewToChartTypeMap,
  IListFiltersChange,
  IListColumn,
  IListProperty,
  ILoadListV2,
  IOKRBaseFilterView,
  ListItem,
  ListOptions,
  ListService,
  ListTypeChangeEventArgs,
  ListTypes,
  ListView,
  IGoalList,
  ListsPropertyNames,
  ListFilterTypes,
  GoogleAggregation,
  ILoadList,
  fieldNamesToBeFormattedAsPercentage,
} from "../..";
import { ChartSettingsLoader } from "../../services/chart-settings-loader";
import { ChartPointsService, ChartPointType } from "@gtmhub/lists/services/chart-points.service";
import { GoalService } from "@gtmhub/goals/goal.service";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { IMetric } from "@gtmhub/okrs/metrics";
import { ExpandNavV2Service } from "@gtmhub/navigation/expand-nav-v2.service";
interface IFormattingChartSettings {
  axes: {
    vAxisFormat: string;
    hAxisFormat: string;
  };
  general: {
    pieSliceTextStyle?: {
      color: string;
    };
    colors?: string[];
    pieHole?: number;
  };
}

export interface IListChartBindings {
  view: ListView;
  filters: IListFiltersChange;
  listId: string;
  listType: ListTypes;
  listProperties: IListProperty[];
  isDeprecateListLoadAPIEnabled: boolean;
}
const MAX_CHART_GROUP_COUNT = 100;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ListChartsComponentCtrl extends IListChartBindings {}

export class ListChartsComponentCtrl extends GtmhubController implements IController {
  public static $inject = [
    "$scope",
    "ListService",
    "ListQueryService",
    "ChartSettingsLoader",
    "ChartSettingsModalOpener",
    "ChartPointsService",
    "$q",
    "$timeout",
    "GoalService",
    "MetricService",
    "ExpandNavV2Service",
  ];
  loading: IIndicator;
  aggregationOrGroupingError: boolean;
  isTooMuchData: boolean;
  chartError: boolean;
  noData: boolean;
  emptyFormattedData: boolean;
  private google: typeof google;

  private dataToDrawFrom: google.visualization.DataTable;
  private partialListData: ListItem[];
  private allListData: ListItem[];
  private loadAllListDataPromise: IPromise<void>;
  private initialLoadMade: boolean;

  constructor(
    private $scope: IScope,
    private listService: ListService,
    private listQueryService: ListQueryService,
    private chartSettingsLoader: ChartSettingsLoader,
    private chartSettingsModalOpener: ChartSettingsModalOpener,
    private chartPointsService: ChartPointsService,
    private $q: IQService,
    private $timeout: ITimeoutService,
    private goalService: GoalService,
    private metricService: MetricService,
    private expandNavV2Service: ExpandNavV2Service
  ) {
    super();
  }

  $onInit(): void {
    this.loading = { progress: true };
    this.$q.all([this.chartSettingsLoader.promiseChartSettings(), this.googleCharts()]).then(([, google]) => {
      this.google = google;

      this.$scope.$on("chartSettingsChange", () => this.onChartSettingsChange());
      this.$scope.$on("listViewChange", (event, view: ListView) => this.onListViewChange(view));
      this.$scope.$on("listTypeChange", (event, args: ListTypeChangeEventArgs) => this.onListTypeChange(args));
      this.setResizeListeners();
      if (this.filters && !this.initialLoadMade) {
        // when filters onChanges triggered first and chartSettings resolved second
        this.initialLoadMade = true;
        this.initialLoadAndDraw();
      }
    });
  }

  // TODO: move into getScripts service and use everywhere we use googleCharts
  googleCharts() {
    return this.$q.when(getGoogleCharts()).then((google) => google);
  }

  onChartSettingsClicked(): void {
    this.chartSettingsModalOpener.open({
      listProperties: this.listProperties,
      listType: this.listType,
      view: this.view,
      chartSettings: this.chartSettingsLoader.chartSettings,
    });
  }

  $onChanges(changesObj: IOnChangesObjectOf<IListChartBindings>): void {
    if (this.chartSettingsLoader.isResolved && changesObj.filters && changesObj.filters.currentValue) {
      // when chartSettings resolved first and filters onChanges triggered second
      this.initialLoadMade = true;
      this.loading = { progress: true };
      this.initialLoadAndDraw();
    }
  }
  private setResizeListeners(): void {
    const onResize = () => {
      if (!this.chartError) {
        this.drawChart();
      }
    };

    this.$scope.$on("navigationToggled", onResize);
    this.expandNavV2Service.subscribe(() => onResize);
    window.addEventListener("resize", onResize);
    this.$scope.$on("$destroy", () => window.removeEventListener("resize", onResize));
  }

  private onChartSettingsChange(): void {
    this.loading = { progress: true };
    this.resetAllErrors();

    this.loadAllListDataPromise.then(() => {
      this.constructChartData(this.allListData).then(() => {
        this.drawChart();
      });
    });
  }

  private initialLoadAndDraw(): void {
    this.loadPartialListData().then(() => {
      this.constructChartData(this.partialListData).then(() => {
        this.drawChart();
      });
    });

    this.loadAllListDataPromise = this.loadAllListData();
  }

  get chartCategory(): IListProperty {
    return this.listProperties.find((p) => p.fieldName === this.chartSettingsLoader.chartSettings.chartCategory);
  }

  get chartValue(): IListProperty {
    return this.listProperties.find((p) => p.fieldName === this.chartSettingsLoader.chartSettings.chartValue);
  }

  private loadPartialListData(): IPromise<void> {
    const columns = [
      { fieldName: this.chartCategory.fieldName, type: this.chartCategory.dataType },
      { fieldName: this.chartValue.fieldName, type: this.chartValue.dataType },
    ];

    if (this.shouldRetrieveGoalForTaskClosedStatus()) {
      columns.push({
        fieldName: ListsPropertyNames.parentId,
        type: ListFilterTypes.id,
      });
    }
    if (this.shouldRetrieveTargetOperatorAdditionalProperties()) {
      columns.push({
        fieldName: "insightName",
        type: ListFilterTypes.alphabetic,
      });
      columns.push({
        fieldName: "manualType",
        type: ListFilterTypes.alphabetic,
      });
    }

    const loadListPromise = this.constructLoadListPromise(columns);

    return loadListPromise.then((collection) => {
      this.partialListData = collection.items;
    });
  }

  private loadAllListData(): IPromise<void> {
    const columns = this.listProperties.map((c) => {
      return { fieldName: c.fieldName, type: c.dataType };
    });

    const loadListPromise = this.constructLoadListPromise(columns);

    return loadListPromise.then((collection) => {
      this.allListData = collection.items;
    });
  }

  private constructListLoadOptions(columns: IListColumn[]): ILoadList {
    return {
      [ListOptions.columns]: columns,
      // to do: remove this when "and-or-filters" are released - BE will respect filters prop only
      // filters may be empty, thus first group can be undefined
      // without '||' check the loaded items differ from those in list
      [ListOptions.filter]: this.filters.listFilters ? this.filters.listFilters[0] || { booleanOperator: "and", ruleBounds: [] } : undefined, // this is a dirty fix until the issue with load list deprecate is solved
      [ListOptions.filters]: this.filters.listFilters,
      [ListOptions.sort]: [],
      [ListOptions.type]: this.listType,
      limit: -1,
      skip: 0,
    };
  }

  private constructLoadListPromise(columns: IListColumn[]): IPromise<ICollection<ListItem>> {
    if (this.isDeprecateListLoadAPIEnabled) {
      const reqParams: ILoadListV2 = {
        listType: this.listType,
        projections: columns,
        filters: this.filters.gridFilters,
        includeRequiredColumns: true,
      };
      return this.listQueryService.loadItems(reqParams);
    }
    const options = this.constructListLoadOptions(columns);
    return this.listService.loadList({ id: this.listId, body: options });
  }

  private constructChartData(items: ListItem[]): Promise<void> {
    if (!items.length) {
      this.noData = true;
      this.chartSettingsModalOpener.close();
      return Promise.resolve();
    }

    this.noData = false;
    const { chartSettings: chartSettings } = this.chartSettingsLoader;

    const neededGoalAndMetricIds: string[] = this.getNeededGoalAndMetricIds(items);

    return this.getGoalsAndMetricsIdMap(neededGoalAndMetricIds).then((goalsAndMetricsIdMap) => {
      const formattedListData = this.getFormattedListData(items, goalsAndMetricsIdMap);

      // we may have items but no data for the selected fields chart settings - for example approval status (if no workflow is set for the session)
      if (!formattedListData.length) {
        console.error(`No values for the selected fields in values setting`);
        this.emptyFormattedData = true;
        return Promise.resolve();
      }

      const dataTable = this.constructDataTable(formattedListData);

      if (!chartSettings.aggregation) {
        if (this.isVisualizationWithoutAggregationForbidden()) {
          console.error(`Can't visualize values without aggregation. Current data type ${this.chartValue.dataType}`);
          this.aggregationOrGroupingError = true;
          return Promise.resolve();
        }
        this.dataToDrawFrom = dataTable;
      } else {
        const googleAggregation = AggregationMap.get(chartSettings.aggregation);
        if (this.isAggregationForbidden(googleAggregation)) {
          console.error(`Can't apply aggregation ${googleAggregation} on data of type ${this.chartValue.dataType}`);
          this.aggregationOrGroupingError = true;
          return Promise.resolve();
        }

        this.performDataGrouping(dataTable, googleAggregation);
      }

      this.checkForMaxGroupCountLimit();
    });
  }

  private getNeededGoalAndMetricIds(items: ListItem[]): string[] {
    let neededGoalAndMetricIds: string[] = [];
    if (this.categoryOrValueIsForField(ListsPropertyNames.goalId)) {
      neededGoalAndMetricIds = [...neededGoalAndMetricIds, ...items.map((item) => item[ListsPropertyNames.goalId]).filter(this.filterEmptyValues())];
    }
    if (this.categoryOrValueIsForField(ListsPropertyNames.parentId) || this.shouldRetrieveGoalForTaskClosedStatus()) {
      neededGoalAndMetricIds = [...neededGoalAndMetricIds, ...items.map((item) => item[ListsPropertyNames.parentId]).filter(this.filterEmptyValues())];
    }

    return neededGoalAndMetricIds;
  }

  private checkForMaxGroupCountLimit(): void {
    const numberOfRowsExceedLimit = this.dataToDrawFrom && this.dataToDrawFrom.getNumberOfRows() > MAX_CHART_GROUP_COUNT;
    if (!this.aggregationOrGroupingError && numberOfRowsExceedLimit) {
      this.isTooMuchData = true;

      const { chartSettings: chartSettings } = this.chartSettingsLoader;
      const parameters = {
        filters: this.filters,
        chartSettings: chartSettings,
        view: this.view,
        listId: this.listId,
        listType: this.listType,
      };
      console.error("The parameters you've chosen provide too much data to show in a chart. Adjust the settings and try again.", parameters);
    }
  }

  private isVisualizationWithoutAggregationForbidden(): boolean {
    return (
      this.chartValue.dataType !== "numeric" &&
      (this.chartValue.dataType !== "date" || (this.chartValue.dataType === "date" && this.view !== ListView.line && this.view !== ListView.bar))
    );
  }

  private isAggregationForbidden(googleAggregation): boolean {
    const nonNumericType = this.chartValue.dataType !== "numeric";
    const nonDateType = this.chartValue.dataType !== "date";
    const minOrMaxAggregation = googleAggregation === "min" || googleAggregation === "max";
    const avgOrSumAggregation = googleAggregation === "avg" || googleAggregation === "sum";

    return (nonNumericType && avgOrSumAggregation) || (nonNumericType && nonDateType && minOrMaxAggregation);
  }

  private getFormattedListData(items: ListItem[], goalsAndMetricsIdMap: IdMap<IOKRBaseFilterView>): [ChartPointType, ChartPointType][] {
    return items.flatMap((item) =>
      this.chartPointsService.listItemToChartPoints(
        item,
        {
          chartValue: this.chartValue,
          chartCategory: this.chartCategory,
        },
        this.listType,
        goalsAndMetricsIdMap
      )
    );
  }

  private performDataGrouping(dataTable: google.visualization.DataTable, googleAggregation: GoogleAggregation): void {
    // validation of data
    try {
      const aggregatedData = this.google.visualization.data.group(
        dataTable,
        [0],
        [{ column: 1, aggregation: this.google.visualization.data[googleAggregation], type: this.getValueType(this.chartValue.dataType, googleAggregation) }]
      );
      this.dataToDrawFrom = aggregatedData;
    } catch (error) {
      console.error(error);
      this.aggregationOrGroupingError = true;
    }
  }

  private constructDataTable(formattedListData: [ChartPointType, ChartPointType][]): google.visualization.DataTable {
    const arrayData = [[this.chartCategory.displayName, this.chartValue.displayName], ...formattedListData];
    return this.google.visualization.arrayToDataTable(arrayData);
  }

  private filterEmptyValues(): (value: string, index: number, array: string[]) => unknown {
    return (item) => item !== undefined && item !== null && item.length > 0;
  }

  private shouldRetrieveGoalForTaskClosedStatus(): boolean {
    return this.listType === ListTypes.Task && this.categoryOrValueIsForField(ListsPropertyNames.closedStatus);
  }

  private shouldRetrieveTargetOperatorAdditionalProperties(): boolean {
    return this.listType === ListTypes["Key Result"] && this.categoryOrValueIsForField(ListsPropertyNames.targetOperator);
  }

  private categoryOrValueIsForField(fieldName: ListsPropertyNames): boolean {
    return this.chartCategory.fieldName === fieldName || this.chartValue.fieldName === fieldName;
  }

  private getGoalsAndMetricsIdMap(neededGoalAndMetricIds: string[]): Promise<IdMap<IOKRBaseFilterView>> {
    if (!neededGoalAndMetricIds.length) {
      return this.$q.resolve({});
    }

    return this.$q.all([this.getGoals(neededGoalAndMetricIds), this.getMetrics(neededGoalAndMetricIds)]).then(([goals, metrics]) => {
      return this.generateGoalsAndMetricsIdMap(goals, metrics);
    });
  }

  private getGoals(neededGoalIds: string[]): IPromise<ICollection<IGoalList>> {
    return this.goalService.getGoalsV2<IGoalList>(this.getGoalsAndMetricsParams(neededGoalIds, "closedStatus"));
  }

  private getMetrics(neededMetricIds: string[]): IPromise<ICollection<IMetric>> {
    return this.metricService.getMetricsV2(this.getGoalsAndMetricsParams(neededMetricIds, "goalClosedStatus"));
  }

  private getGoalsAndMetricsParams(neededGoalAndMetricIds: string[], closedStatusFieldName: string): IRestLayerRequest {
    return {
      fields: `id,name,${closedStatusFieldName}`,
      filter: {
        _id: { $in: neededGoalAndMetricIds },
      },
    };
  }

  private generateGoalsAndMetricsIdMap(goals: ICollection<IGoalList>, metrics: ICollection<IMetric>): IdMap<IOKRBaseFilterView> {
    const goalsAndMetricsIdMap: IdMap<IOKRBaseFilterView> = {};
    goals.items.forEach((goal) => {
      goalsAndMetricsIdMap[goal.id] = {
        id: goal.id,
        name: goal.name,
        closedStatus: goal.closedStatus,
      };
    });
    metrics.items.forEach((metric) => {
      goalsAndMetricsIdMap[metric.id] = {
        id: metric.id,
        name: metric.name,
        closedStatus: metric.goalClosedStatus,
      };
    });
    return goalsAndMetricsIdMap;
  }

  private getValueType(dataType: string, googleAggregation: GoogleAggregation): string {
    if (dataType === "date" && googleAggregation !== "count") {
      return "date";
    } else {
      return "number";
    }
  }

  private drawChart(): void {
    if (this.google && this.view !== ListView.list && !this.aggregationOrGroupingError && !this.isTooMuchData && !this.noData && !this.emptyFormattedData) {
      this.chartError = false;
      // The timeout is needed since the chart may be drawn before the container is adjusted and the chart would look buggy
      this.$timeout(() => {
        const chartType = chartViewToChartTypeMap.get(this.view);
        const chart = new this.google.visualization[chartType](document.getElementById("chart"));

        // when the cart cannot be drawn
        this.google.visualization.events.addListener(chart, "error", (error) => {
          console.error(error);
          this.chartError = true;
          delete this.loading;
        });

        this.google.visualization.events.addListener(chart, "ready", () => {
          delete this.loading;
        });

        const additionalSettings = this.constructAdditionalSettings();
        this.applyCustomFormatters();

        chart.draw(this.dataToDrawFrom, {
          width: "100%",
          height: "100%",
          hAxis: {
            baselineColor: "none", // makes the Ox line not bold
            format: additionalSettings.axes.vAxisFormat,
          },
          vAxis: {
            baselineColor: "none", // makes the Oy line not bold
            format: additionalSettings.axes.hAxisFormat,
          },
          ...additionalSettings.general,
        });
      }, 200);
    } else {
      delete this.loading;
    }
  }

  private onListViewChange(view: ListView): void {
    this.loading = { progress: true };
    this.view = view;
    this.drawChart();
  }

  private onListTypeChange(args: ListTypeChangeEventArgs): void {
    this.loading = { progress: true };
    this.listType = args.listType;
    this.listProperties = args.listProperties;
  }

  private resetAllErrors(): void {
    this.aggregationOrGroupingError = false;
    this.emptyFormattedData = false;
    this.isTooMuchData = false;
    this.chartError = false;
  }

  private constructAdditionalSettings(): IFormattingChartSettings {
    const shouldDrawDonut = this.view === ListView.pie && this.chartSettingsLoader.chartSettings.visualization === "Donut";
    const shouldUseColor = this.view !== ListView.pie;
    const shouldUsePieSliceTextStyle = shouldDrawDonut && this.dataToDrawFrom.getNumberOfRows() === 1;
    return {
      axes: {
        vAxisFormat: fieldNamesToBeFormattedAsPercentage.has(this.chartCategory.fieldName) ? "#'%'" : "",
        hAxisFormat: fieldNamesToBeFormattedAsPercentage.has(this.chartValue.fieldName) ? "#'%'" : "",
      },
      general: {
        ...(shouldDrawDonut ? { pieHole: 0.4 } : {}),
        ...(shouldUseColor ? { colors: [this.chartSettingsLoader.chartSettings.color] } : {}),
        ...(shouldUsePieSliceTextStyle
          ? {
              pieSliceTextStyle: {
                color: "black",
              },
            }
          : {}),
      },
    };
  }

  private applyCustomFormatters(): void {
    if (fieldNamesToBeFormattedAsPercentage.has(this.chartCategory.fieldName)) {
      this.formatPercentageColumn(this.chartCategory.fieldName, 0);
    }

    if (fieldNamesToBeFormattedAsPercentage.has(this.chartValue.fieldName)) {
      this.formatPercentageColumn(this.chartValue.fieldName, 1);
    }
  }

  private formatPercentageColumn(fieldName: string, columndIndex: number): void {
    const formatter = new google.visualization.NumberFormat({
      fractionDigits: fieldName === "progress" ? 1 : 0,
      suffix: "%",
    });

    formatter.format(this.dataToDrawFrom, columndIndex);
  }
}

export const ListChartsComponent: IComponentOptions = {
  templateUrl: require("./list-chart.component.html"),
  controller: ListChartsComponentCtrl,
  bindings: {
    view: "<",
    listId: "<",
    listType: "<",
    listProperties: "<",
    filters: "<",
    isDeprecateListLoadAPIEnabled: "<",
  },
};
